import { supabase } from "@/lib/supabase/client";
import { TaskContent, TaskId } from "@/types/content-types";
import { useState, useEffect, useRef, forwardRef, useImperativeHandle, useMemo, memo, useCallback, ComponentProps, ComponentRef, ReactNode } from "react";
import { AudioButton } from "../AudioButton";
import { TaskMarkdown } from "../TaskMarkdown";
import { Textarea } from "../TextArea";
import { cn } from "@/lib/utils";
import { Values } from "./Types";
import { Field } from "../Field";
import { useChapterHandler } from "@/contexts/chapterHandler";
import { TaskTextarea } from "./TaskTextarea";
import { TaskAudioSegment } from "./TaskAudioSegment";
import { isSoleBodyEntry } from "@/lib/tts/taskContentShape";
import { remarkTtsSegments } from "@/lib/tts/remarkTtsSegments";
import { TTS_STORAGE_BUCKET, ttsStoragePrefix } from "@/lib/tts/ttsAudioPath";
import type { PluggableList } from "unified";

// Stable reference so passing it as extraRemarkPlugins doesn't defeat react-markdown's processor memoization.
const TTS_EXTRA_REMARK_PLUGINS: PluggableList = [remarkTtsSegments];

export type InputsRecord = Record<number, HTMLTextAreaElement | HTMLInputElement>;

export type TaskContentProps = {
    title?: string;
    content: TaskContent;
    taskId?: TaskId;
    defaultInputValues?: Values;
    values?: Values;
    onValuesChanged?: (values: Values) => void;
    disabled?: boolean;
};

type TaskContentElementRef = {
    inputs: InputsRecord;
    inputCount: number;
};
type MarkdownFieldProps = Omit<ComponentProps<typeof Field>, "onChange"> & {
    type?: string;
    defaultValue?: string;
    onChange: (value: string) => void;
};
const MarkdownField = memo(forwardRef<ComponentRef<typeof Field>, MarkdownFieldProps>(({ type, defaultValue, onChange, ...props }, ref) => {
    const [value, setValue] = useState(defaultValue ?? "");

    const inputProperties = type === "checkbox" || type === "radio"
        ? { checked: value === "true" }
        : { value: value };

    return <Field
        {...props}
        {...inputProperties}
        type={type}
        ref={ref}
        onChange={(newValue) => {
            const normalizedValue = type === "checkbox" || type === "radio"
                ? newValue.checked.toString()
                : newValue.value;
            setValue(normalizedValue);
            onChange(normalizedValue);
        }}
    />;
}));

// ── MarkdownItem lives outside TaskContentElement so its identity is stable ──

type MarkdownItemProps = {
    content: string;
    defaultValues: React.MutableRefObject<Values>;
    inputRefCounter: React.MutableRefObject<number>;
    inputsRef: React.MutableRefObject<InputsRecord>;
    onCommit: (id: string, value: string) => void;
    onRegisterInput: (id: string, element: HTMLTextAreaElement | null) => void;
    disabled?: boolean;
    /** Set only for eligible (new-format) content — enables TTS segmentation. */
    taskId?: TaskId;
    audioTitle?: string;
    availableTtsSegments?: Set<number> | null;
};

const MarkdownItem = memo(({ content, defaultValues, inputRefCounter, inputsRef, onCommit, onRegisterInput, disabled, taskId, audioTitle, availableTtsSegments }: MarkdownItemProps) => {
    // Store latest callbacks in refs so the components map never needs to be recreated
    const onCommitRef = useRef(onCommit);
    useEffect(() => { onCommitRef.current = onCommit; }, [onCommit]);

    const onRegisterRef = useRef(onRegisterInput);
    useEffect(() => { onRegisterRef.current = onRegisterInput; }, [onRegisterInput]);

    const reset = useRef(null);
    const components = useMemo<ComponentProps<typeof TaskMarkdown>["components"]>(() => ({
        field(props) {
            const id = props?.id;

            return <MarkdownField
                {...props}
                id={id}
                label={(id ? "" : "[Saknar id] ") + props.label}
                ref={(element) => {
                    inputsRef.current[id] = element;
                    inputRefCounter.current++;
                }}
                defaultValue={defaultValues.current?.[props.id] ?? "false"}
                onChange={(newValue) => {
                    onCommitRef.current(id, newValue);
                }}
                disabled={disabled}
            />;
        },
        "task-textarea"(props) {
            const id = props?.id;
            if (!id) {
                console.warn("TaskTextarea is missing id");
            }

            return <TaskTextarea
                {...props}
                label={(id ? "" : "[Saknar id] ") + props.label}
                id={id}
                ref={(ref) => {
                    inputsRef.current[id] = ref?.textarea ?? null;
                    inputRefCounter.current++;
                    reset.current = ref?.resetValue;
                    // Use the ref so we always call the latest version
                    onRegisterRef.current(id, ref?.textarea ?? null);
                }}
                defaultValue={defaultValues.current?.[props.id] ?? ""}
                onChange={(newValue) => {
                    onCommitRef.current(id, newValue);
                }}
                disabled={disabled}
            />;
        },
        ...(taskId != null ? {
            "tts-segment"(props: { index: number; children?: ReactNode; }) {
                return <TaskAudioSegment
                    taskId={taskId}
                    index={props.index}
                    hasAudio={availableTtsSegments?.has(+props.index) ?? false}
                    audioTitle={audioTitle}
                >
                    {props.children}
                </TaskAudioSegment>;
            }
        } : {})
        // Ref objects are stable — taskId/audioTitle/availableTtsSegments are the only non-ref deps
    }), [defaultValues, disabled, inputRefCounter, inputsRef, taskId, audioTitle, availableTtsSegments]);

    return <TaskMarkdown components={components} extraRemarkPlugins={taskId != null ? TTS_EXTRA_REMARK_PLUGINS : undefined}>{content}</TaskMarkdown>;
});

// ─────────────────────────────────────────────────────────────────────────────

const audioBlobUrlCache: Map<string, string> = new Map();

const TaskContentElement = forwardRef<TaskContentElementRef, TaskContentProps>(
    function TaskContentElement({
        title,
        content,
        taskId,
        defaultInputValues,
        values: controlledValues,
        onValuesChanged,
        disabled,
    }, ref) {
        const [audioUrls, setAudioUrls] = useState<string[]>(content?.map(cont => cont.audioSrc) ?? []);
        const [loadingAudios, setLoadingAudios] = useState([]);
        const [availableTtsSegments, setAvailableTtsSegments] = useState<Set<number> | null>(null);
        // inputRefIndices are outdated but should be kept until all tasks are migrated to pure md for textareas)
        const [inputRefIndices, setInputRefIndices] = useState<Record<number, number>>({});
        const [localValues, setLocalValues] = useState<Values>(defaultInputValues ?? {});

        const inputRefCounter = useRef(0);
        const inputsRef = useRef<InputsRecord>({});

        // Reset counters synchronously during render when content changes
        // This runs before children render, so increments happen after the reset
        const prevContentRef = useRef(content);
        if (prevContentRef.current !== content) {
            prevContentRef.current = content;
            inputRefCounter.current = 0;
            inputsRef.current = {};
        }

        const values = controlledValues ?? localValues;

        // Tried to find a better way to parse the title using markdown but was taking a while and
        // isn't worth spending a lot of time on rn. Might be worth it in the future if titles get more complex.
        const { currentSection } = useChapterHandler();
        const audioTitle = useMemo(() => title?.replace(":section-title", currentSection?.section?.title), [currentSection?.section?.title, title]);

        useImperativeHandle(ref, () => ({
            get inputs() { return inputsRef.current; },
            // Counted from the registered elements so that re-renders don't inflate it
            get inputCount() { return Object.values(inputsRef.current).filter(Boolean).length; }
        }));

        // Instead of doing this, it would be nicer to just add the missing keys to the 
        // values object once.
        // To know when all input refs have been assigned, there could be a counter ref
        // that is incremented whenever a field or text area is added. When the number of
        // input refs equals the counted number, the empty object with all keys can be created.
        const emptyValuesRef = useRef<Values>({});
        const prevInputsRef = useRef<InputsRecord>();
        function emptyValues() {
            if (prevInputsRef.current !== inputsRef.current) {
                emptyValuesRef.current = Object.fromEntries(
                    Object.entries(inputsRef.current)
                        .map(([id, element]) => [
                            id,
                            element.type !== "checkbox"
                                ? null
                                : "false"
                        ])
                );
                prevInputsRef.current = inputsRef.current;
            }
            return emptyValuesRef.current;
        }

        // Keep latest values/onValuesChanged in a ref so handleCommit is stable
        const valuesRef = useRef(values);
        useEffect(() => { valuesRef.current = values; }, [values]);

        const onValuesChangedRef = useRef(onValuesChanged);
        useEffect(() => { onValuesChangedRef.current = onValuesChanged; }, [onValuesChanged]);

        // Stable callbacks — these never change identity, so MarkdownItem's useMemo
        // deps (inputRefCounter) are always stable refs
        const handleCommit = useCallback((id: string, value: string) => {
            const newValues = { ...emptyValues(), ...valuesRef.current };
            newValues[id] = value;
            onValuesChangedRef.current?.(newValues);
            setLocalValues(newValues);
        }, []);

        const handleRegisterInput = useCallback((id: string, element: HTMLTextAreaElement | null) => {
            inputsRef.current[id] = element;
            inputRefCounter.current++;
        }, []);

        useEffect(() => {
            // Input indices (outdated but should be kept until all tasks are migrated to pure md for textareas)
            // inputRefIndices maps the index of a content entry to the index of its input
            // Not all entries have inputs, so their indices can't be used directly
            let inputCounter = 0;
            const newInputRefIndices: typeof inputRefIndices = {};
            content?.forEach((entry, index) => {
                if (!entry.hasTextBox) return;
                newInputRefIndices[index] = inputCounter;
                inputCounter++;
            });
            setInputRefIndices(newInputRefIndices);

            // Audio
            const supabaseAudioSrcList = content?.map(cont => cont.supabaseAudioSrc);
            supabaseAudioSrcList.forEach((audioSrc, index) => {
                if (!audioSrc) return;

                const cachedBlobUrl = audioBlobUrlCache.get(audioSrc);
                if (cachedBlobUrl) {
                    setAudioUrls(prev => {
                        const newList = [...prev];
                        newList[index] = cachedBlobUrl;
                        return newList;
                    });
                    return;
                }
                setLoadingAudios(prev => {
                    const newList = [...prev];
                    newList[index] = true;
                    return newList;
                });
                (async () => {
                    const { data, error } = await supabase.storage.from("audio files").download(audioSrc);
                    if (error) {
                        console.error(error);
                        return;
                    }
                    const blobUrl = URL.createObjectURL(data);
                    audioBlobUrlCache.set(audioSrc, blobUrl);
                    setAudioUrls(prev => {
                        const newList = [...prev];
                        newList[index] = blobUrl;
                        return newList;
                    });
                    setLoadingAudios(prev => {
                        const newList = [...prev];
                        newList[index] = false;
                        return newList;
                    });
                })();
            });
        }, [content]);

        // Which TTS segment indices actually have generated audio — a lightweight
        // metadata call, not a download, so icons can render without prefetching audio.
        useEffect(() => {
            if (taskId == null) {
                setAvailableTtsSegments(null);
                return;
            }
            (async () => {
                const { data, error } = await supabase.storage.from(TTS_STORAGE_BUCKET).list(ttsStoragePrefix(taskId));
                if (error) {
                    console.error(error);
                    setAvailableTtsSegments(new Set());
                    return;
                }
                const indices = data
                    .map(file => parseInt(file.name, 10))
                    .filter(index => !Number.isNaN(index));
                setAvailableTtsSegments(new Set(indices));
            })();
        }, [taskId]);

        return (
            <div className="px-3 bg-background rounded-lg overflow-x-clip">
                {title && <TaskTitle title={title} />}
                {content?.map((question, index) => {
                    const body = Array.isArray(question.body) ? question.body : [question.body];
                    const parts = body.map((text, index) => ({ text: text, index }));
                    const ttsEligible = index === 0 && taskId != null && isSoleBodyEntry(question);
                    return (
                        <div key={index} className="mb-4 flex flex-col">
                            {(audioUrls?.[index] || loadingAudios[index]) && (
                                <AudioButton
                                    disabled={loadingAudios[index]}
                                    buttonText={loadingAudios[index] ? "Hämtar Ljud..." : "Lyssna"}
                                    size="sm"
                                    variant="step-current"
                                    audioUrl={audioUrls?.[index]}
                                    audioTitle={audioTitle}
                                    className="self-end"
                                />
                            )}
                            {question.title && (
                                <div className="text-base font-medium text-step-current mb-2 mt-3">
                                    <TaskMarkdown>{question.title}</TaskMarkdown>
                                </div>
                            )}
                            {parts?.map(({ text, index }) => (
                                <div key={index} className="text-base text-muted-foreground mb-2">
                                    <MarkdownItem
                                        content={text}
                                        defaultValues={valuesRef}
                                        inputRefCounter={inputRefCounter}
                                        inputsRef={inputsRef}
                                        onCommit={handleCommit}
                                        onRegisterInput={handleRegisterInput}
                                        disabled={disabled}
                                        taskId={ttsEligible ? taskId : undefined}
                                        audioTitle={audioTitle}
                                        availableTtsSegments={ttsEligible ? availableTtsSegments : undefined}
                                    />
                                </div>
                            ))}
                            {question.imagePath && (
                                <img src={question.imagePath} />
                            )}
                            {/* This code is outdated but should be kept until all tasks are migrated to pure md for textareas*/}
                            {question.hasTextBox && inputRefIndices && (() => {
                                return (
                                    <Textarea
                                        rows={question.lines}
                                        ref={(element) => {
                                            if (!element || inputRefIndices[index] == null) return;
                                            inputRefCounter.current++;
                                            inputsRef.current[inputRefIndices[index]] = element;
                                        }}
                                        value={values?.[inputRefIndices[index]] ?? ""}
                                        className={cn(
                                            "min-h-[120px] text-base ring-1 ring-offset-2 ring-step-current focus-visible:ring-step-current",
                                            question.lines && "min-h-0"
                                        )}
                                        onChange={(e) => {
                                            const newValues = { ...emptyValues(), ...values };
                                            newValues[inputRefIndices[index]] = e.target.value;
                                            onValuesChanged(newValues);
                                            setLocalValues(newValues);
                                        }}
                                        disabled={disabled}
                                    />
                                );
                            })()}
                        </div>
                    );
                })}
            </div>
        );
    });

export function TaskTitle({ title }: { title: string }) {
    return (
        <div className="text-base font-medium text-step-current mb-2">
            <TaskMarkdown>{title}</TaskMarkdown>
        </div>
    );
}

export { TaskContentElement };